import { c as _c } from "react/compiler-runtime";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { bookAppointment } from "@/src/actions/scheduling";
import { toast } from "@/src/hooks/use-toast";
import { updateAppointmentsCache } from "@/src/lib/data-fetchers/appointments-fetchers";
import { GetSchedulingPractitionersErrors, GetSchedulingLocationsErrors, SchedulingError, SchedulingErrorDomain, createApiError } from "@/src/lib/data-fetchers/scheduling-fetchers-errors";
import { logger } from "@/src/lib/logging/logger";
import { Appointment, AppointmentCreatedBy } from "@/src/models/appointment";
import { AppointmentSlot, type AvailabilityCheckParams } from "@/src/models/comms/scheduling-schemas";
import { Patient } from "@/src/models/patient";
import { Practitioner } from "@/src/models/practitioner";
import { DEFAULT_SMS_INBOX_AVAILABILITY_APPOINTMENT_TYPES } from "@/src/models/scheduling-capabilities";
import { SchedulingRecommendation } from "@/src/models/scheduling-recommendation";
import { getReferralSchedulingRecommendation, verifyReferralSchedulingRecommendation, type VerifySchedulingRecommendationPayload } from "@/src/requests/referral";
import { getPatientAppointments, getPractitioners, getLocations, getSchedulingCapabilities, checkAvailability, isEstablishedPatient, type IsEstablishedPatientResponse } from "@/src/requests/scheduling";
export const SCHEDULING_CACHE_KEY = "scheduling";
const ESTABLISHED_PATIENT_ESTABLISHED_STALE_TIME_MS = 7 * 24 * 60 * 60 * 1000;
const ESTABLISHED_PATIENT_NEW_STALE_TIME_MS = 24 * 60 * 60 * 1000;
function getIsEstablishedPatientStaleTime(data: IsEstablishedPatientResponse | null | undefined): number {
  if (!data) {
    return 0;
  }

  // Established patients only fall out of status once attended visits age past the
  // 3-year lookback, so a longer cache is safe. New patients can flip to
  // established after a completed visit, so refresh more often.
  return data.isEstablishedPatient ? ESTABLISHED_PATIENT_ESTABLISHED_STALE_TIME_MS : ESTABLISHED_PATIENT_NEW_STALE_TIME_MS;
}

// Shared query key so callers that mutate the verification (the inline referral controls and the
// dedicated verification task) update the same cached recommendation, keeping the highlighted state
// consistent across views.
export const getReferralSchedulingRecommendationQueryKey = (referralId: string | null | undefined) => ["referralSchedulingRecommendation", referralId] as const;
export function useReferralSchedulingRecommendation(referralId, schedulingEnabled) {
  const $ = _c(15);
  let t0;
  if ($[0] !== referralId) {
    t0 = getReferralSchedulingRecommendationQueryKey(referralId);
    $[0] = referralId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== referralId) {
    t1 = async () => {
      if (!referralId) {
        return null;
      }
      return await getReferralSchedulingRecommendation(referralId);
    };
    $[2] = referralId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = Boolean(referralId) && schedulingEnabled;
  let t3;
  if ($[4] !== t0 || $[5] !== t1 || $[6] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2,
      staleTime: Infinity
    };
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const query = useQuery(t3);
  const t4 = query.data || null;
  let t5;
  if ($[8] !== query.error || $[9] !== query.isError || $[10] !== query.isFetching || $[11] !== query.isPending || $[12] !== query.refetch || $[13] !== t4) {
    t5 = {
      recommendation: t4,
      isFetching: query.isFetching,
      isPending: query.isPending,
      isError: query.isError,
      error: query.error,
      refetch: query.refetch
    };
    $[8] = query.error;
    $[9] = query.isError;
    $[10] = query.isFetching;
    $[11] = query.isPending;
    $[12] = query.refetch;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
}

// Lets a reviewer mark the referral's scheduling recommendation correct/incorrect.
// The verified value gates whether the cron sends automated SMS outreach.
export function useVerifySchedulingRecommendation(referralId) {
  const $ = _c(10);
  const queryClient = useQueryClient();
  let t0;
  let t1;
  if ($[0] !== queryClient || $[1] !== referralId) {
    t0 = async payload => {
      if (!referralId) {
        throw new Error("Cannot verify scheduling recommendation without a referral id");
      }
      const loadedRecommendation = queryClient.getQueryData(getReferralSchedulingRecommendationQueryKey(referralId));
      if (!loadedRecommendation) {
        throw new Error("Cannot verify scheduling recommendation before one has loaded");
      }
      return verifyReferralSchedulingRecommendation(referralId, {
        ...payload,
        recommendationId: loadedRecommendation.id
      });
    };
    t1 = updated => {
      queryClient.setQueryData(getReferralSchedulingRecommendationQueryKey(referralId), updated);
    };
    $[0] = queryClient;
    $[1] = referralId;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  let t2;
  if ($[4] !== referralId) {
    t2 = error => {
      logger.error("Error verifying scheduling recommendation", {
        referralId,
        error
      });
      toast({
        title: "Couldn't save verification",
        description: "Please try again.",
        variant: "destructive"
      });
    };
    $[4] = referralId;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t0 || $[7] !== t1 || $[8] !== t2) {
    t3 = {
      mutationFn: t0,
      onSuccess: t1,
      onError: t2
    };
    $[6] = t0;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return useMutation(t3);
}
export function usePatientAppointments(patientId) {
  const $ = _c(18);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== patientId) {
    t0 = [SCHEDULING_CACHE_KEY, "appointments", patientId];
    $[0] = patientId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== patientId || $[3] !== queryClient) {
    t1 = async () => {
      if (!patientId) {
        return [] as Appointment[];
      }
      const appointments = await getPatientAppointments(patientId);
      updateAppointmentsCache(queryClient, appointments);
      return appointments;
    };
    $[2] = patientId;
    $[3] = queryClient;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const t2 = Boolean(patientId);
  let t3;
  if ($[5] !== t0 || $[6] !== t1 || $[7] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2,
      staleTime: Infinity
    };
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const query = useQuery(t3);
  let t4;
  if ($[9] !== query.data) {
    t4 = query.data || [];
    $[9] = query.data;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== query.error || $[12] !== query.isError || $[13] !== query.isFetching || $[14] !== query.isPending || $[15] !== query.refetch || $[16] !== t4) {
    t5 = {
      appointments: t4,
      isFetching: query.isFetching,
      isPending: query.isPending,
      isError: query.isError,
      error: query.error,
      refetch: query.refetch
    };
    $[11] = query.error;
    $[12] = query.isError;
    $[13] = query.isFetching;
    $[14] = query.isPending;
    $[15] = query.refetch;
    $[16] = t4;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  return t5;
}
export function useIsEstablishedPatient(patientId, t0) {
  const $ = _c(17);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const options = t1;
  let t2;
  let t3;
  if ($[2] !== patientId) {
    t2 = [SCHEDULING_CACHE_KEY, "is-established-patient", patientId];
    t3 = async () => {
      if (!patientId) {
        return null;
      }
      return isEstablishedPatient(patientId);
    };
    $[2] = patientId;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  const t4 = Boolean(patientId) && (options.enabled ?? true);
  let t5;
  if ($[5] !== t2 || $[6] !== t3 || $[7] !== t4) {
    t5 = {
      queryKey: t2,
      queryFn: t3,
      enabled: t4,
      staleTime: _temp
    };
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const query_0 = useQuery(t5);
  const t6 = query_0.data?.isEstablishedPatient ?? null;
  let t7;
  if ($[9] !== query_0.error || $[10] !== query_0.isError || $[11] !== query_0.isFetching || $[12] !== query_0.isLoading || $[13] !== query_0.isPending || $[14] !== query_0.refetch || $[15] !== t6) {
    t7 = {
      isEstablishedPatient: t6,
      isFetching: query_0.isFetching,
      isPending: query_0.isPending,
      isLoading: query_0.isLoading,
      isError: query_0.isError,
      error: query_0.error,
      refetch: query_0.refetch
    };
    $[9] = query_0.error;
    $[10] = query_0.isError;
    $[11] = query_0.isFetching;
    $[12] = query_0.isLoading;
    $[13] = query_0.isPending;
    $[14] = query_0.refetch;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
}
function _temp(query) {
  return getIsEstablishedPatientStaleTime(query.state.data);
}
export function useSchedulingCapabilities(t0) {
  const $ = _c(11);
  const enabled = t0 === undefined ? true : t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [SCHEDULING_CACHE_KEY, "capabilities"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== enabled) {
    t2 = {
      queryKey: t1,
      queryFn: getSchedulingCapabilities,
      enabled,
      staleTime: 300000
    };
    $[1] = enabled;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const query = useQuery(t2);
  const t3 = query.data?.emrSchedulingSupported ?? false;
  const t4 = query.data?.locationBasedScheduling ?? false;
  const t5 = query.data?.smsInboxAvailabilityAppointmentTypes ?? DEFAULT_SMS_INBOX_AVAILABILITY_APPOINTMENT_TYPES;
  let t6;
  if ($[3] !== query.error || $[4] !== query.isError || $[5] !== query.isPending || $[6] !== query.refetch || $[7] !== t3 || $[8] !== t4 || $[9] !== t5) {
    t6 = {
      emrSchedulingSupported: t3,
      locationBasedScheduling: t4,
      smsInboxAvailabilityAppointmentTypes: t5,
      isPending: query.isPending,
      isError: query.isError,
      error: query.error,
      refetch: query.refetch
    };
    $[3] = query.error;
    $[4] = query.isError;
    $[5] = query.isPending;
    $[6] = query.refetch;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  return t6;
}
export function usePractitioners(patientId?: string, options: {
  enabled?: boolean;
} = {}) {
  const query = useQuery({
    queryKey: [SCHEDULING_CACHE_KEY, "practitioners", patientId],
    queryFn: async () => {
      if (!patientId) {
        return [] as Practitioner[];
      }
      try {
        const practitioners = await getPractitioners(patientId);
        if (practitioners.length === 0) {
          throw new SchedulingError(SchedulingErrorDomain.GET_SCHEDULING_PRACTITIONERS, GetSchedulingPractitionersErrors.NO_PROVIDERS);
        }
        return practitioners;
      } catch (error) {
        throw createApiError(error, SchedulingErrorDomain.GET_SCHEDULING_PRACTITIONERS);
      }
    },
    enabled: Boolean(patientId) && (options.enabled ?? true),
    staleTime: Infinity
  });
  return {
    practitioners: query.data || [],
    isFetching: query.isFetching,
    isPending: query.isPending,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch
  };
}
export function useLocations(patientId: string) {
  const query = useQuery({
    queryKey: [SCHEDULING_CACHE_KEY, "locations", patientId],
    queryFn: async () => {
      try {
        const locations = await getLocations(patientId);
        if (locations.length === 0) {
          throw new SchedulingError(SchedulingErrorDomain.GET_SCHEDULING_LOCATIONS, GetSchedulingLocationsErrors.NO_LOCATIONS);
        }
        return locations;
      } catch (error) {
        throw createApiError(error, SchedulingErrorDomain.GET_SCHEDULING_LOCATIONS);
      }
    },
    enabled: Boolean(patientId),
    staleTime: Infinity
  });
  return {
    locations: query.data || [],
    isFetching: query.isFetching,
    isPending: query.isPending,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch
  };
}

// Mutation so we can trigger it on button click
export function useAvailabilityCheck() {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp2,
      onSuccess: (appointmentSlots, variables) => {
        const queryKey = [SCHEDULING_CACHE_KEY, "availability", variables.patientId, variables.appointmentType, variables.practitionerIds.sort(), variables.locationIds.sort(), variables.scheduleAvailability];
        queryClient.setQueryData(queryKey, appointmentSlots);
      },
      onError: _temp3
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
}
function _temp3(error, variables_0) {
  logger.error("Error checking availability", {
    patientId: variables_0.patientId,
    appointmentType: variables_0.appointmentType,
    error
  });
  const apiError = createApiError(error, SchedulingErrorDomain.CHECK_AVAILABILITY);
  toast({
    title: "Error checking availability",
    description: apiError.message,
    variant: "destructive"
  });
}
async function _temp2(t0) {
  const {
    patientId,
    appointmentType,
    practitionerIds,
    locationIds,
    scheduleAvailability
  } = t0;
  if (!patientId || practitionerIds.length === 0 || locationIds.length === 0 || !appointmentType) {
    return;
  }
  return checkAvailability(patientId, appointmentType, practitionerIds, locationIds, scheduleAvailability);
}
export function useBookAppointmentMutation(t0) {
  const $ = _c(11);
  const createdBy = t0 === undefined ? AppointmentCreatedBy.VALERIE_SMS : t0;
  const queryClient = useQueryClient();
  let t1;
  if ($[0] !== createdBy) {
    t1 = async t2 => {
      const {
        appointmentSlot,
        patient
      } = t2;
      const result = await bookAppointment(appointmentSlot, patient, createdBy);
      if (!result) {
        throw new Error("Failed to book appointment");
      }
      return result;
    };
    $[0] = createdBy;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  let t3;
  let t4;
  if ($[2] !== queryClient) {
    t2 = async t5 => {
      const {
        patient: patient_0
      } = t5;
      await queryClient.cancelQueries({
        queryKey: [SCHEDULING_CACHE_KEY, "appointments", patient_0.id]
      });
      const previousAppointments = queryClient.getQueryData([SCHEDULING_CACHE_KEY, "appointments", patient_0.id]) || [];
      return {
        previousAppointments
      };
    };
    t3 = (error, variables, context) => {
      logger.error("Error booking appointment", {
        patientId: variables.patient.id,
        appointmentSlotId: variables.appointmentSlot.ehrId,
        error
      });
      if (context) {
        queryClient.setQueryData([SCHEDULING_CACHE_KEY, "appointments", variables.patient.id], context.previousAppointments);
      }
      const apiError = createApiError(error, SchedulingErrorDomain.BOOK_APPOINTMENT);
      toast({
        title: "Error booking appointment",
        description: apiError.message,
        variant: "destructive"
      });
    };
    t4 = (appointment, variables_0) => {
      logger.info("Successfully booked appointment", {
        appointmentId: appointment.id,
        patientId: variables_0.patient.id
      });
      queryClient.setQueryData([SCHEDULING_CACHE_KEY, "appointments", variables_0.patient.id], t6 => {
        const oldAppointments = t6 === undefined ? [] : t6;
        const existingIndex = oldAppointments.findIndex(existing => existing.ehrId === appointment.ehrId || existing.id === appointment.id);
        if (existingIndex >= 0) {
          return oldAppointments.map((existing_0, index) => index === existingIndex ? appointment : existing_0);
        }
        return [appointment, ...oldAppointments];
      });
      queryClient.invalidateQueries({
        queryKey: [SCHEDULING_CACHE_KEY, "availability"]
      });
    };
    $[2] = queryClient;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
  } else {
    t2 = $[3];
    t3 = $[4];
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t1 || $[7] !== t2 || $[8] !== t3 || $[9] !== t4) {
    t5 = {
      mutationFn: t1,
      onMutate: t2,
      onError: t3,
      onSuccess: t4
    };
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return useMutation(t5);
}